import { useEffect, useRef, useState } from 'react';
import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui/primitives';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ReactElement, ReactNode } from 'react';

export const MetricValueText = ({
  children,
  nowrap = false,
  align = 'left',
  color = 'text.secondary',
  fontSize,
  lineHeight,
}: {
  children: ReactNode;
  nowrap?: boolean;
  align?: 'left' | 'right';
  color?: string;
  fontSize?: string;
  lineHeight?: string | number;
}): ReactElement => (
  <Text
    role="bodyMuted"
    sx={(muiTheme) => ({
      color,
      fontSize: fontSize ?? muiTheme.typography.subtitle1.fontSize,
      lineHeight: lineHeight ?? muiTheme.typography.subtitle1.lineHeight,
      textAlign: align,
      whiteSpace: nowrap ? 'nowrap' : undefined,
    })}
  >
    {children}
  </Text>
);

const renderSecondaryValue = (secondaryValue: ReactNode): ReactNode => {
  if (typeof secondaryValue !== 'string') {
    return secondaryValue;
  }
  const match = /^([\d,]+)\s+(.+)$/.exec(secondaryValue);
  if (!match) {
    return secondaryValue;
  }
  const [, value, label] = match;
  return (
    <>
      <Text
        component="span"
        role="bodyMuted"
        color="textSecondary"
        sx={(muiTheme) => ({
          fontSize: muiTheme.typography.pxToRem(17),
          fontWeight: 400,
        })}
      >
        {value}
      </Text>
      <Text
        component="span"
        role="bodyMuted"
        color="textSecondary"
        sx={(muiTheme) => ({ fontSize: muiTheme.typography.pxToRem(17) })}
      >
        {` ${label}`}
      </Text>
    </>
  );
};

export const StatusMetricCardValueRow = ({
  count,
  percentage,
  secondaryValue,
}: {
  count: MetricCardItem['count'];
  percentage: MetricCardItem['percentage'];
  secondaryValue: MetricCardItem['secondaryValue'];
}): ReactElement => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const mainValueRef = useRef<HTMLDivElement | null>(null);
  const secondaryValueRef = useRef<HTMLDivElement | null>(null);
  const [stackSecondaryValue, setStackSecondaryValue] = useState(false);
  const hasSecondaryValue =
    secondaryValue !== null && secondaryValue !== undefined && secondaryValue !== '';

  useEffect(() => {
    if (!hasSecondaryValue) {
      setStackSecondaryValue(false);
      return;
    }

    const measureLayout = (): void => {
      const rowElement = rowRef.current;
      const mainValueElement = mainValueRef.current;
      const secondaryElement = secondaryValueRef.current;
      if (!rowElement || !mainValueElement || !secondaryElement) return;

      const styles = window.getComputedStyle(rowElement);
      const columnGap = Number.parseFloat(styles.columnGap || '0');
      const requiredWidth = mainValueElement.scrollWidth + secondaryElement.scrollWidth + columnGap;
      setStackSecondaryValue(requiredWidth > rowElement.clientWidth);
    };

    measureLayout();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      measureLayout();
    });
    if (rowRef.current) {
      resizeObserver.observe(rowRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [count, hasSecondaryValue, percentage, secondaryValue]);

  return (
    <Box
      ref={rowRef}
      sx={{
        display: 'grid',
        gridTemplateColumns: stackSecondaryValue ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto',
        alignItems: 'center',
        columnGap: s('md'),
        rowGap: stackSecondaryValue ? s('xxs') : s('none'),
        pt: s('none'),
        minWidth: 0,
      }}
    >
      <Box
        ref={mainValueRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: `calc(${s('sm')} + ${s('xs')})`,
          pl: s('xs'),
          minWidth: 0,
        }}
      >
        <Text role="sectionTitle">{count}</Text>
        {percentage ? <MetricValueText>{percentage}</MetricValueText> : null}
      </Box>
      {hasSecondaryValue ? (
        <Box
          ref={secondaryValueRef}
          sx={{
            pt: stackSecondaryValue ? s('none') : '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: stackSecondaryValue ? 'flex-start' : 'flex-end',
            justifySelf: stackSecondaryValue ? 'start' : 'end',
            minWidth: 0,
            textAlign: stackSecondaryValue ? 'left' : 'right',
            pl: stackSecondaryValue ? s('xs') : s('none'),
          }}
        >
          <MetricValueText
            nowrap={!stackSecondaryValue}
            align={stackSecondaryValue ? 'left' : 'right'}
            color="text.secondary"
            fontSize="16px"
            lineHeight={1.2}
          >
            {renderSecondaryValue(secondaryValue)}
          </MetricValueText>
        </Box>
      ) : null}
    </Box>
  );
};
